import React from 'react';
import classes from './PostCardMediaPreviewPinterest.module.scss';
import { SocialProfileWithBoards } from '../../../../hooks/useCategoriesProfilesWithBoards';
import { PostCardPreviewMediaDataType } from '../PostCardMediaPreview/PostCardMediaPreview.view';
import { serializeText, serializeTextToHtml } from '../../TextEditor/helpers/serializer';
import { SocialProfile } from '../../../../components/socialProfiles/SocialProfile';
import classNames from 'classnames';
import { Badge } from '../../../../components/containers/Badge';
import { BadgeColorsEnum, BadgeSizesEnum } from '../../../../constants/badge';
import { Icon } from '../../../../components/images/Icon/Icon';
import { IconsEnum } from '../../../../constants/icons';
import { Loader } from '../../../../components/containers/Loader';
import { InvalidMediaSet } from '../PostCardMediaPreview/InvalidMediaSet';
import { MediaType, Network, PostMultiMediaAttachMode, SocialOAuthProvider } from '../../../../models/graphql';
import { SingularValidator } from '../../validators/helpers/singular-validator';

export type PostCardMediaPreviewPinterestProfile = {
  socialProfile: SocialProfileWithBoards & { __typename: 'PinterestSocialProfile' };
  selectedBoardId?: string;
  title?: string;
};

export type PostCardMediaPreviewPinterestViewProps = PostCardPreviewMediaDataType &
  PostCardMediaPreviewPinterestProfile;

export const PostCardMediaPreviewPinterestView = (props: PostCardMediaPreviewPinterestViewProps): JSX.Element => {
  const { text, media, socialProfile, link, selectedBoardId, title } = props;
  const loadingLinkMetadata = link && link.linkMetadata === undefined;
  const linkMetadata = link?.linkMetadata;
  const serializedText = serializeText(text);
  const hasText = !!serializedText || !!title;
  const selectedBoardName = socialProfile.boards.find(board => board.externalId === selectedBoardId)?.name;
  const hasInvalidMedia =
    SingularValidator.noMixedMediaInvalid(media, PostMultiMediaAttachMode.MULTIPLE) ||
    SingularValidator.pinterestMediaLimitInvalid(media, PostMultiMediaAttachMode.MULTIPLE);

  const linkElement = link ? (
    <a href={link.url} className={classNames(classes.LinkBadge, 'align-left', 'align-middle')}>
      <div className={classes.LinkBadgeArrowIcon}>
        <Icon name={IconsEnum.arrowPN} size={'medium'} />
      </div>
      <div className={classes.LinkBadgeLinkText}>
        {link.url.replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/$/, '')}
      </div>
    </a>
  ) : null;

  const invalidMediaBlock = (
    <InvalidMediaSet className={classNames(classes.NoContent, hasText ? classes.NoContentWithText : null)} />
  );

  return (
    <>
      <div className={classNames(classes.SocialProfileWrapper, 'grid-x', 'align-justify', 'align-middle')}>
        <div className={classNames('cell shrink', selectedBoardName ? classes.SocialProfileWrapperMaxWidth : null)}>
          <SocialProfile
            socialProfile={{
              id: socialProfile.id,
              provider: SocialOAuthProvider.PINTEREST,
              network: Network.PINTEREST,
              name: socialProfile.displayName,
              type: ''
            }}
            disconnected={!socialProfile.connected}
            sublabel={true}
            size={'medium'}
            label={true}
            image={socialProfile.photoUrl}
          />
        </div>
        {selectedBoardName ? (
          <div className={classNames('cell', 'auto', classes.PnBoardNameWrapper)}>
            <div className={'grid-x align-right'}>
              <div className={classNames('cell shrink', classes.PnBoardNameWrapperWidth)}>
                <Badge color={BadgeColorsEnum.GREY20} size={BadgeSizesEnum.LARGE} text={selectedBoardName} />
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {media.length ? (
        <div className={classNames(classes.MediaWrapper, { [classes.MediaWrapperNoText]: !hasText })}>
          {hasInvalidMedia ? (
            invalidMediaBlock
          ) : (
            <>
              {media[0].type === MediaType.VIDEO ? (
                media[0].url ? (
                  <video controls={false} src={media[0].url} />
                ) : (
                  <img src={media[0].thumbnailUrl} alt={'pinterest'} />
                )
              ) : (
                <img src={media[0].url} alt={'pinterest'} />
              )}
              {linkElement}
              {media[0].gif ? (
                <div className={classes.GifMarker}>
                  <Icon name={IconsEnum.gifBadge} size={'large'} />
                </div>
              ) : null}
              {media[0].type === MediaType.VIDEO ? (
                <div className={classes.MediaMarker}>
                  <Icon name={IconsEnum.play} size={'large'} />
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : !link ? (
        <div className={classes.MediaWrapper}>
          <div
            className={classNames(
              classes.NoContent,
              'grid-x',
              'align-middle',
              'align-center',
              hasText ? classes.NoContentWithText : null
            )}>
            <p>You need to add an image/GIF/video for Pinterest</p>
          </div>
        </div>
      ) : null}
      {(loadingLinkMetadata || linkMetadata) && !media.length && link ? (
        <div className={classNames(classes.MediaWrapper, { [classes.MediaWrapperNoText]: !hasText })}>
          {loadingLinkMetadata ? <Loader variation={'dark'} size={'small'} type={'inline'} /> : null}
          {linkMetadata && linkMetadata.images.length ? (
            <>
              <img src={linkMetadata.images[0]} alt={'pinterest'} />
              {linkElement}
            </>
          ) : (
            <div
              className={classNames(
                classes.NoContent,
                'grid-x',
                'align-middle',
                'align-center',
                hasText ? classes.NoContentWithText : null
              )}>
              <p>You need to add an image/GIF/video for Pinterest</p>
            </div>
          )}
        </div>
      ) : null}
      {hasText ? (
        <div className={classes.TextWrapper}>
          {title ? <div className={classes.TextTitle}>{title}</div> : null}
          {serializedText ? (
            <div
              dangerouslySetInnerHTML={{
                __html: serializeTextToHtml(
                  text,
                  undefined,
                  classNames('no-b-m', classes.Text, { [classes.TextWithTitle]: !!title })
                )
              }}></div>
          ) : null}
        </div>
      ) : null}
    </>
  );
};
